import experiment from 'experiment';
import { languages } from '#packages/util';
import React, { type FC, useEffect } from 'react';
import styles from './AppVersionsList.scss';
import {
  BackButton,
  Composites,
  Divider,
  Preloader,
  SortByDragList,
  SortByDragListCustomItemRow,
  Text,
  Tooltip,
} from '@wix/wix-base-ui';
import type { VersionsResponse } from '@wix/ambassador-app-service-webapp/types';
import installedVersionIndication from '../installedVersionIndication';
import { useAppVersionsListContext } from './AppVersionsListContext';
import { useSelectedApp } from '../selectedAppContext/selectedAppContext';
import {
  InstallStatus,
  usePrivateApps,
} from '../privateAppsProvider/privateAppsProvider';
import * as util from '#packages/util';
import { translate } from '#packages/i18n';
import {
  isAutoUpdatePending,
  getAppVersion,
  isTestVersionOnlyApp,
} from '../../utils/utils';
import UpdateInProgressIndication from '../common/updateInProgressIndication/updateInProgressIndication';
import type { AppData } from '../privateAppsProvider/privateAppsProvider';

function getDefaultVersion(app: AppData): string {
  if (experiment.isOpen('se_testAppWithDevVersion')) {
    if (util.appStudioUtils.isTestVersion(app)) {
      return app.appFields.installedVersion;
    }

    if (!util.appStudioUtils.isAppMajorVersionBuilt(app.latestVersion)) {
      return util.appStudioUtils.TEST_VERSION.LATEST;
    }

    return app.latestVersion;
  }

  return isTestVersionOnlyApp(app)
    ? util.appStudioUtils.TEST_VERSION.LATEST
    : app.latestVersion;
}

function onBackBtnSelectedVesion(app: AppData): string {
  if (experiment.isOpen('se_testAppWithDevVersion')) {
    if (util.appStudioUtils.isTestVersion(app)) {
      return app.appFields.installedVersion;
    }

    if (!util.appStudioUtils.isAppMajorVersionBuilt(app.latestVersion)) {
      return util.appStudioUtils.TEST_VERSION.LATEST;
    }

    return getAppVersion(app) || app.latestVersion;
  }

  return util.appStudioUtils.isTestVersion(app) || isTestVersionOnlyApp(app)
    ? util.appStudioUtils.TEST_VERSION.LATEST
    : getAppVersion(app) || app.latestVersion;
}

const locale = languages.getLanguageCode();

const AppVersionsList: FC<{}> = () => {
  const isTestAppWithDevVersionEnabled = experiment.isOpen(
    'se_testAppWithDevVersion',
  );

  const { selectedVersionListApp, setSelectedVersionListApp } =
    useAppVersionsListContext();
  const { setSelectedApp, selectedApp } = useSelectedApp();
  const { installedApps, availableApps } = usePrivateApps();

  const { versions, app } = selectedVersionListApp;
  const installedVersion = app?.appFields?.installedVersion;

  const onVersionClicked = (versionId: string) => {
    const installStatus =
      installedVersion === versionId || getAppVersion(app) === versionId
        ? InstallStatus.Installed
        : InstallStatus.Versioned;
    setSelectedApp(
      {
        ...app,
        installStatus,
      },
      versionId,
    );
  };

  useEffect(() => {
    const selectedVersion = getDefaultVersion(app);

    onVersionClicked(selectedVersion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions]);

  const SortByDragListProps = {
    stopPropagationOnContextMenuToggle: true,
    menuActionsOverrides: {
      toggleEditLabel: {
        enable: false,
      },
    },
    removeContextMenu: true,
    draggable: false,
    selectable: true,
    selectItem: (item: { id: string }) => onVersionClicked(item.id),
  };

  const getLocaleDateString = (date: Date) =>
    date instanceof Date
      ? date.toLocaleDateString(locale, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : undefined;

  const renderVersionContentAfter = (version: string) => {
    if (
      version === getAppVersion(app) &&
      !util.appStudioUtils.isTestVersion(app)
    ) {
      return renderInstalledVersionIndication();
    }
    if (
      version === app.latestVersion &&
      !util.appStudioUtils.isTestVersion(app) &&
      isAutoUpdatePending(
        getAppVersion(app),
        app.latestVersion,
        installedVersion,
      )
    ) {
      return <UpdateInProgressIndication version={app.latestVersion} />;
    }
    return null;
  };

  const renderVersion = ({ version, dateCreated }: VersionsResponse) => {
    return (
      <SortByDragListCustomItemRow
        dataHook={'app-version-item'}
        id={version}
        key={version}
        label={util.appStudioUtils.formatVersion(version)}
        value={getLocaleDateString(dateCreated)}
        isSelected={version === selectedApp.selectedVersion}
        contentAfter={renderVersionContentAfter(version)}
      />
    );
  };

  const renderTestVersionButton = () => (
    <SortByDragList {...SortByDragListProps}>
      {[
        <SortByDragListCustomItemRow
          className={styles.testVersionItem}
          id={
            isTestAppWithDevVersionEnabled &&
            util.appStudioUtils.isTestVersion(app)
              ? app.appFields.installedVersion
              : util.appStudioUtils.TEST_VERSION.LATEST
          }
          key={
            isTestAppWithDevVersionEnabled &&
            util.appStudioUtils.isTestVersion(app)
              ? app.appFields.installedVersion
              : util.appStudioUtils.TEST_VERSION.LATEST
          }
          label={translate(
            'blocks-private-apps.AppDiscovery_App_Versions_Test_Version_Title',
          )}
          value={getLocaleDateString(versions?.[0]?.dateCreated)}
          isSelected={util.appStudioUtils.isTestVersion(
            selectedApp.selectedVersion,
          )}
          contentAfter={
            util.appStudioUtils.isTestVersion(app)
              ? renderInstalledVersionIndication()
              : null
          }
        />,
      ]}
    </SortByDragList>
  );

  const renderVersionsList = () => (
    <>
      {renderTestVersionButton()}
      <Divider className={styles.versionsListTestDivider} />
      <SortByDragList
        dataHook={'app-versions-list'}
        {...SortByDragListProps}
        noBorderTop={true}
      >
        {versions.map((version) => renderVersion(version))}
      </SortByDragList>
    </>
  );

  const renderInstalledVersionIndication = () => (
    <Tooltip
      content="blocks-private-apps.AppDiscovery_App_Versions_Installed_Version_Tooltip"
      className={styles.versionInstalledIcon}
    >
      {installedVersionIndication()}
    </Tooltip>
  );

  return (
    <div className={styles.showAllVersionsForPrivateApp}>
      <Composites.ButtonLeft>
        <BackButton
          label={app.appDefinitionName}
          onClick={() => {
            setSelectedVersionListApp(null);
            setSelectedApp(
              [...installedApps, ...availableApps].find(
                ({ appDefinitionId }) =>
                  appDefinitionId === app.appDefinitionId,
              ),
              onBackBtnSelectedVesion(app),
            );
          }}
          shouldTranslate={false}
        />
      </Composites.ButtonLeft>
      <div className={styles.availableVersions}>
        <Text weight="bold" size="small">
          blocks-private-apps.AppDiscovery_App_Versions_Title
        </Text>
      </div>
      <div className={styles.versionsList}>
        {versions ? (
          renderVersionsList()
        ) : (
          <div className={styles.loaderContainer}>
            <Preloader className={'medium'} />
          </div>
        )}
      </div>
    </div>
  );
};

export default util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  null,
  null,
)(AppVersionsList);
